import { Card, Table } from "@caprikorn/dynamo-ui";
import type { FetchDataProps } from "@caprikorn/dynamo-ui/lib/components/dataDisplay/Table/Table.types";
import { getFirstDayOfMonth, getLastDayOfMonth } from "@caprikorn/dynamo-utils";
import { type FC, useState } from "react";
import { useParams } from "react-router-dom";

import { FileCsv, FilePdf } from "@icons";
import type { UrlParam } from "@router/router.types";
import type {
  BillPaymentStatus,
  CustomerBill,
} from "@services/apiEcms/apiEcms.types";
import { useGetChildEntityBillingAccountBills } from "@services/apiEcms/useGetChildEntityBillingAccountBills";
import { usePostDocumentsDownload } from "@services/apiEcms/usePostDocumentsDownload";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import { AccountBalance } from "./AccountBalance/AccountBalance";
import { useDocsColumns } from "./InvoiceAndPaymentsDetails.columns";
import classes from "./InvoiceAndPaymentsDetails.module.css";
import { getDefaultDateRange } from "./InvoiceAndPaymentsDetails.utils";

const MIN_ACCOUNTING_REFERENCE_LENGTH = 12;

const initialFilters: FetchDataProps = {
  pageIndex: 0,
  pageSize: 10,
  globalFilter: "",
  columnFilters: [
    {
      id: "accountingDocumentDate",
      value: getDefaultDateRange(),
    },
  ],
  sorting: [],
};
const getRowId = (row: CustomerBill) => row.accountingDocumentReference;

export const InvoiceAndPaymentsDetails: FC = () => {
  const { t } = useCustomTranslation("main");
  const { customerId = "", contractId = "" } = useParams<UrlParam>();
  const [filters, setFilters] = useState<FetchDataProps>(initialFilters);

  const [startDate, endDate] =
    (filters.columnFilters.find((c) => c.id === "accountingDocumentDate")
      ?.value as [string, string]) ?? [];

  const params = {
    customerId,
    contractId,
    page: filters.pageIndex + 1,
    size: filters.pageSize,
    "start-date": startDate ? getFirstDayOfMonth(startDate) : undefined,
    "end-date": endDate ? getLastDayOfMonth(endDate) : undefined,
    status: filters.columnFilters.find(
      (column) => column.id === "billPaymentStatus",
    )?.value as BillPaymentStatus,
    "accounting-document-reference":
      filters.globalFilter?.trim().length >= MIN_ACCOUNTING_REFERENCE_LENGTH
        ? filters.globalFilter.trim()
        : undefined,
  };

  const { data, isError, isPending } =
    useGetChildEntityBillingAccountBills(params);
  const {
    mutate: downloadFile,
    variables: downloadAccountReferenceVariables,
    isPending: isDownloadAccountReferencePending,
  } = usePostDocumentsDownload();
  const columns = useDocsColumns();

  return (
    <Card>
      <Table
        columns={columns}
        data={data?.bills || []}
        fetchData={setFilters}
        getRowId={getRowId}
        initialColumnFilters={initialFilters.columnFilters}
        initialPageIndex={initialFilters.pageIndex}
        initialPageSize={initialFilters.pageSize}
        isError={isError}
        isLoading={isPending}
        rowActions={(row) =>
          (["pdf", "csv"] as const).map((extension) => ({
            label: t(
              "perimeter.sites.invoiceAndPaymentsDetails.accountingDocumentsTable.rowActions.download",
              {
                format: extension.toLocaleUpperCase(),
              },
            ),
            icon: extension === "csv" ? FileCsv : FilePdf,
            isLoading:
              isDownloadAccountReferencePending &&
              row.accountingDocumentReference ===
                downloadAccountReferenceVariables?.reference &&
              downloadAccountReferenceVariables?.extension === extension,
            onClick: ({
              accountingDocumentReference: reference,
            }: CustomerBill) =>
              downloadFile({
                extension,
                customerId,
                contractId,
                reference,
                family: "invoice",
                last: false,
              }),
          }))
        }
        totalCount={data?.pagination?.recordsCount || 0}
      >
        <Table.ActionBar>
          <div className={classes.invoiceHeader}>
            <Table.ActionBar.Title
              title={t(
                "perimeter.sites.invoiceAndPaymentsDetails.accountingDocumentsTable.title",
              )}
            />
            <AccountBalance />
          </div>
          <Table.ActionBar.FilterBar
            hasGlobalFilter
            searchPlaceholder={t(
              "perimeter.sites.invoiceAndPaymentsDetails.accountingDocumentsTable.searchPlaceholder",
            )}
          />
        </Table.ActionBar>

        <Table.Content />
        <Table.Pagination withPageSizeSelector />
      </Table>
    </Card>
  );
};
